import { queryClient } from "@/App";
import ContaViagem from "@/Models/Domain/Viagem/Financeiro/ContaViagem";
import { EnumContaViagemSituacao } from "@/Models/Domain/Viagem/Financeiro/ContaViagem/ContaViagemSituacao";
import { Movimentacao } from "@/Models/Financeiro/Conta/Movimentacao";
import WebHttpErroResponse, { IWebHttpErroResponse } from "@/Models/Utils/Error/WebHttpErroResponse";
import Pagina, { PaginaInfo } from "@/Models/Utils/Pagina/Pagina";
import { Axios } from "@/Services/Axios";
import { notificarErroNaRequisicao } from "@/Utils/Notificacoes";
import RequestUtilitario, { MutationBasicaParam, QueryBasicaParam } from "@/Utils/RequisicaoUtilitario";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";

export type ContaViagemSalvarFormType = {
    contaViagem:ContaViagem;
    movimentacaoInicial?:Movimentacao;
}
export function useContaViagemSalvar({
    onSuccess,
    ...rest
}:MutationBasicaParam<ContaViagemSalvarFormType, AxiosResponse<void>|AxiosResponse<number>>) {
    return useMutation({
        ...rest,
        mutationFn : (dataRequest) => {
            return dataRequest.contaViagem.codigo !== undefined && dataRequest.contaViagem.codigo > 0
                ? Axios.put(`viagem/financeiro/conta/${dataRequest.contaViagem.codigo}`, dataRequest.contaViagem)
                : Axios.post<number>(`viagem/financeiro/conta`, dataRequest)
            ;
        },
        onSuccess : (retorno, entrada, context) => {
            queryClient.invalidateQueries({ queryKey: ["Viagem.Financeiro.ContaViagem.Listar"] });
            onSuccess?.(retorno, entrada, context);
        }
    });
}
export type ContaViagemListarFiltros = {
    paginaInfo?:PaginaInfo,
    codCentrosCustos?:number[],
    contaViagem?:ContaViagem,
    ativo?:boolean
};
export function useContaViagemListar({
    paginaInfo,
    codCentrosCustos,
    contaViagem,
    ativo,
    ...rest
}:QueryBasicaParam<AxiosResponse<Pagina<ContaViagem>>, Pagina<ContaViagem>, ContaViagemListarFiltros>) {
    const queryString = RequestUtilitario.objectToQueryString({
        paginaInfo,
        codCentrosCustos,
        contaViagem,
        ativo
    });

    return useQuery({
        ...rest,
        queryKey : [ "Viagem.Financeiro.ContaViagem.Listar", `Viagem.Financeiro.ContaViagem.Listar.Pagina?${queryString}` ],
        queryFn : () => Axios.get<Pagina<ContaViagem>>(`viagem/financeiro/conta/pagina?${queryString}`),
        select(data) {
            data.data.registros.forEach(m => {
                m.dataCriacao = new Date(m.dataCriacao as any);
            });

            return data.data;
        },
        staleTime: 1000 * 60 * 0.25
    });
}

export function useContaViagemListarTodos({
    codCentrosCustos,
    contaViagem,
    ativo,
    ...rest
}:QueryBasicaParam<AxiosResponse<Array<ContaViagem>>, Array<ContaViagem>, Omit<ContaViagemListarFiltros, "paginaInfo">>) {
    const queryString = RequestUtilitario.objectToQueryString({
        codCentrosCustos,
        contaViagem,
        ativo
    });

    return useQuery({
        ...rest,
        queryKey : [ "Viagem.Financeiro.ContaViagem.Listar", `Viagem.Financeiro.ContaViagem.ListarTodos?${queryString}` ],
        queryFn : () => Axios.get<Array<ContaViagem>>(`viagem/financeiro/conta?${queryString}`),
        select(data) {
            data.data.forEach(m => {
                m.dataCriacao = new Date(m.dataCriacao as any);
            });

            return data.data;
        },
        staleTime: 1000 * 60 * 0.25
    });
}

export type ContaViagemBuscarFiltros = {
    codigo : number;
    ativo ?: boolean;
};
export function useContaViagemBuscar({
    codigo,
    ativo,
    ...rest
}:QueryBasicaParam<AxiosResponse<ContaViagem|undefined>, ContaViagem|undefined, ContaViagemBuscarFiltros>) {
    return useQuery({
        ...rest,
        queryKey : [ "Viagem.Financeiro.ContaViagem", 'Viagem.Financeiro.ContaViagem.Buscar', `Viagem.Financeiro.ContaViagem.Buscar.${codigo}?=ativo${ativo}` ],
        queryFn : () => Axios.get<ContaViagem|undefined>(`viagem/financeiro/conta/${codigo}?ativo=${ativo ?? ''}`),
        select(data) {
            if (data.data === undefined || data.data === null) return undefined;

            data.data.dataCriacao = new Date(data.data.dataCriacao as any);

            return data.data;
        },
        staleTime: 1000 * 60 * 0.25
    });
}

export type AlterarSituacaoType = {
    codContaViagem:number;
    codSituacao:EnumContaViagemSituacao;
    codContaSaldoResidual?:number;
}
export function useContaViagemAlterarSituacao({
    onSuccess,
    ...rest
}:MutationBasicaParam<AlterarSituacaoType, AxiosResponse<void>>) {
    return useMutation({
        ...rest,
        mutationFn : (dataRequest) => {
            if (dataRequest.codSituacao == EnumContaViagemSituacao.EmLancamento)
                return Axios.post<void>(`viagem/financeiro/conta/${dataRequest.codContaViagem}/abrir`, dataRequest);
            else if (dataRequest.codSituacao == EnumContaViagemSituacao.Lancado)
                return Axios.post<void>(`viagem/financeiro/conta/${dataRequest.codContaViagem}/lancar`, dataRequest);
            else if (dataRequest.codSituacao == EnumContaViagemSituacao.Encerrado)
                return Axios.post<void>(`viagem/financeiro/conta/${dataRequest.codContaViagem}/encerrar`, dataRequest);

            const erro = {
                response : {
                    data : new WebHttpErroResponse({
                            mensagem : "Situação informada não existe",
                            status : "400",
                            statusCode : 400
                        } as IWebHttpErroResponse
                    )
                }
            } as AxiosError;

            notificarErroNaRequisicao(erro);

            return Promise.reject(erro);
        },
        onSuccess : (retorno, entrada, context) => {
            queryClient.invalidateQueries({ queryKey: ["Viagem.Financeiro.ContaViagem.Listar"] });
            onSuccess?.(retorno, entrada, context);
        }
    });
}